import { useCallback, useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import type { IconProps } from "@similarweb/icons";
import {
    ChevronDown,
    DesktopAndMobile,
    DesktopComputer,
    Image,
    MobileDevice,
    ExpandDiagonal,
    Download,
    OpenInNew,
    Copy,
} from "@similarweb/icons";
import type {
    AdIntelligenceEnumsPlacementType,
    AdIntelligenceEnumsScreenshotFilter,
    AdIntelligenceRecordsDetailedVerification,
} from "@similarweb/pro-api-client";
import { Drawer } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import type { SegmentedControlValue } from "@similarweb/ui-components";
import {
    Favicon,
    Pagination,
    PaginationInput,
    PixelPlaceholderLoader,
    SegmentedControl,
    Tooltip,
} from "@similarweb/ui-components";
import { Flex, Stack, Text, TruncatedText } from "@similarweb/ui-components/foundations";

import { useToasts } from "app/common/hooks";
import { NumberFormatter } from "app/common/utils/number-formatter";
import { IconButton } from "app/components/icon-button";
import DurationService from "app/services/DurationService";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";
import { allTrackers } from "app/services/track/track";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../hooks/useDisplayAdvertiserPublisherDataAndParams";

import { ImagePreviewDialog } from "./ImagePreviewDialog";
import { useVerificationDetails, VERIFICATION_TAB_KEYS } from "./useVerificationDetails";
import { formatDateRange, formatPositionLabel, openInNewTab } from "./verification-widget.utils";

const formatCount = (value: number): string => NumberFormatter.toAbbr(value);

export interface IVerificationCellData {
    publisher: string;
    dateFrom?: string;
    dateTo?: string;
    expectedCount?: number;
}

interface IVerificationDrawerProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    cellData: IVerificationCellData | null;
    advertiser: string;
    position?: AdIntelligenceEnumsPlacementType[] | null;
}

const ChevronIcon = styled.span`
    display: flex;
    transform: rotate(-90deg);
    transition: transform 0.2s ease;
`;

const StyledDetails = styled.details`
    border: 1px solid ${colorsPalettes.carbon[100]};
    border-radius: 6px;

    &[open] ${ChevronIcon} {
        transform: rotate(0deg);
    }
`;

const StyledSummary = styled.summary`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    height: 44px;
    box-sizing: border-box;
    cursor: pointer;
    list-style: none;

    &::-webkit-details-marker {
        display: none;
    }
`;

const SummaryLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
`;

const DetailsContent = styled(Stack)`
    padding: 16px;
`;

const CapitalizedText = styled(Text)`
    text-transform: capitalize;
`;

const SCREENSHOT_MAX_HEIGHT = 360;

const ScreenshotScrollArea = styled.div`
    max-height: ${SCREENSHOT_MAX_HEIGHT}px;
    overflow: auto;
    border-radius: 4px;
    border: 1px solid ${colorsPalettes.carbon[100]};
`;

const ScreenshotImage = styled.img`
    display: block;
    max-width: 100%;
    cursor: zoom-in;
`;

const ITEMS_PER_PAGE = 10;

const DEFAULT_SKELETON_ITEMS_COUNT = 6;

const clampSkeletonCount = (count: number | undefined) => {
    if (!count || count < 1) {
        return DEFAULT_SKELETON_ITEMS_COUNT;
    }
    return Math.min(count, ITEMS_PER_PAGE);
};

const SkeletonRow = styled(Flex)`
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    height: 44px;
    min-height: 44px;
    box-sizing: border-box;
    border: 1px solid ${colorsPalettes.carbon[100]};
    border-radius: 6px;
`;

const useClipboard = () => {
    const { translate } = useTranslate();
    const { showErrorToast, showSuccessToast } = useToasts();
    const copy = useCallback(
        async (text: string) => {
            try {
                await window.navigator.clipboard.writeText(text);
                showSuccessToast(translate("ad.intelligence.verification.drawer.copy.success"));
            } catch {
                showErrorToast(translate("ad.intelligence.verification.drawer.copy.error"));
            }
        },
        [showErrorToast, showSuccessToast, translate],
    );

    return copy;
};

const VerificationDrawerSkeleton = ({ itemsCount }: { itemsCount: number }) => (
    <Stack gap="12px">
        <PixelPlaceholderLoader
            width="100%"
            height={36}
        />
        <Stack gap="8px">
            {Array.from({ length: itemsCount }).map((_, index) => (
                <SkeletonRow key={index}>
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        <PixelPlaceholderLoader
                            width={12}
                            height={12}
                        />
                        <PixelPlaceholderLoader
                            width={64}
                            height={14}
                        />
                    </Flex>
                    <PixelPlaceholderLoader
                        width={96}
                        height={14}
                    />
                </SkeletonRow>
            ))}
        </Stack>
    </Stack>
);

const ICON_COLOR = colorsPalettes.carbon[300];
const TEXT_COLOR = colorsPalettes.carbon[500];

const renderDeviceIcon = (device: string, size: IconProps["size"] = "sm") => {
    const normalized = device.toLowerCase();
    if (normalized === "mobile") {
        return (
            <MobileDevice
                size={size}
                color={ICON_COLOR}
            />
        );
    }
    if (normalized === "desktop") {
        return (
            <DesktopComputer
                size={size}
                color={ICON_COLOR}
            />
        );
    }
    return (
        <DesktopAndMobile
            size={size}
            color={ICON_COLOR}
        />
    );
};

const DeviceIconTriggerWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    cursor: help;
`;

const DeviceIcon = ({
    device,
    translate,
    size,
}: {
    device: string;
    translate: (key: string) => string;
    size?: IconProps["size"];
}) => {
    const normalized = device.toLowerCase();
    const tooltipKey =
        normalized === "mobile"
            ? "ad.intelligence.verification.drawer.device.mobile"
            : normalized === "desktop"
              ? "ad.intelligence.verification.drawer.device.desktop"
              : "ad.intelligence.verification.drawer.device.all";

    return (
        <Tooltip content={translate(tooltipKey)}>
            <DeviceIconTriggerWrapper>{renderDeviceIcon(device, size)}</DeviceIconTriggerWrapper>
        </Tooltip>
    );
};

const ScreenshotIndicatorWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    cursor: help;
`;

const RowNumber = styled(Text)`
    min-width: 24px;
    color: ${colorsPalettes.carbon[400]};
`;

const VerificationDetailItem = ({
    item,
    publisher,
    onPreview,
    rowNumber,
}: {
    item: AdIntelligenceRecordsDetailedVerification;
    publisher: string;
    onPreview: (src: string) => void;
    rowNumber: number;
}) => {
    const { translate } = useTranslate();
    const copyToClipboard = useClipboard();
    const hasScreenshot = Boolean(item.screenshot);
    const { campaignName, campaignPage } = item;

    const handleOpenPreview = useCallback(() => {
        if (item.screenshot) {
            allTrackers.dispatchTrackEvent("Verification widget", "open", "Screenshot open");
            onPreview(item.screenshot);
        }
    }, [item.screenshot, onPreview]);

    const handleDownloadScreenshot = useCallback(() => {
        if (!item.screenshot) {
            return;
        }
        allTrackers.dispatchTrackEvent("Verification widget", "download", "Screenshot download");
        const link = document.createElement("a");
        link.href = item.screenshot;
        link.download = `screenshot-${item.dateTime}.png`;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.click();
    }, [item.screenshot, item.dateTime]);

    return (
        <StyledDetails data-automation="verification-detail-item">
            <StyledSummary>
                <SummaryLeft>
                    <ChevronIcon>
                        <ChevronDown
                            size="sm"
                            color={ICON_COLOR}
                        />
                    </ChevronIcon>
                    <RowNumber fontVariant="body2">{rowNumber}.</RowNumber>
                    <TruncatedText fontVariant="input2Bold">
                        {campaignName || translate("ad.intelligence.verification.drawer.default_title")}
                    </TruncatedText>
                </SummaryLeft>

                {item.dateTime ? (
                    <Text
                        fontVariant="input2Bold"
                        color={ICON_COLOR}
                    >
                        {dayjs.utc(item.dateTime).format("DD MMM")}
                    </Text>
                ) : null}
                {item.device ? (
                    <DeviceIcon
                        device={item.device}
                        translate={translate}
                    />
                ) : null}
                {hasScreenshot ? (
                    <Tooltip content={translate("ad.intelligence.verification.drawer.has_screenshot_tooltip")}>
                        <ScreenshotIndicatorWrapper>
                            <Image
                                size="sm"
                                color={ICON_COLOR}
                            />
                        </ScreenshotIndicatorWrapper>
                    </Tooltip>
                ) : null}
            </StyledSummary>
            <DetailsContent gap="12px">
                <Stack gap="4px">
                    <Text
                        fontVariant="body2"
                        color={TEXT_COLOR}
                    >
                        <strong>{translate("ad.intelligence.verification.drawer.detail.publisher")}</strong>
                    </Text>
                    <Text fontVariant="body2">
                        <span>{publisher}</span>
                        <IconButton
                            $padding="sm"
                            onClick={() => openInNewTab(publisher)}
                        >
                            <OpenInNew size="xs" />
                        </IconButton>
                        <IconButton
                            $padding="sm"
                            onClick={(_e) => void copyToClipboard(publisher)}
                        >
                            <Copy size="xs" />
                        </IconButton>
                    </Text>
                </Stack>
                <Stack gap="4px">
                    <Text
                        fontVariant="body2"
                        color={TEXT_COLOR}
                    >
                        <strong>{translate("ad.intelligence.verification.drawer.detail.landing_page")}</strong>
                    </Text>
                    <Text
                        fontVariant="body2"
                        overflowWrap="break-word"
                    >
                        <span>{campaignPage || "-"}</span>
                        {campaignPage ? (
                            <>
                                <IconButton
                                    $padding="sm"
                                    onClick={() => openInNewTab(campaignPage)}
                                >
                                    <OpenInNew size="xs" />
                                </IconButton>
                                <IconButton
                                    $padding="sm"
                                    onClick={(_e) => void copyToClipboard(campaignPage)}
                                >
                                    <Copy size="xs" />
                                </IconButton>
                            </>
                        ) : null}
                    </Text>
                </Stack>
                <Stack gap="4px">
                    <Text
                        fontVariant="body2"
                        color={TEXT_COLOR}
                    >
                        <strong>{translate("ad.intelligence.verification.drawer.detail.campaign_name")}</strong>
                    </Text>
                    <Text
                        fontVariant="body2"
                        overflowWrap="break-word"
                    >
                        {campaignName}
                    </Text>
                </Stack>
                <Flex
                    gap="24px"
                    alignItems="center"
                >
                    {item.device ? (
                        <Stack
                            gap="4px"
                            flex="1"
                        >
                            <Text
                                fontVariant="body2"
                                color={TEXT_COLOR}
                            >
                                <strong>{translate("ad.intelligence.verification.drawer.detail.device")}</strong>
                            </Text>
                            <Flex
                                alignItems="center"
                                gap="6px"
                            >
                                <DeviceIcon
                                    device={item.device}
                                    translate={translate}
                                    size="xs"
                                />
                                <CapitalizedText fontVariant="body2">{item.device}</CapitalizedText>
                            </Flex>
                        </Stack>
                    ) : null}
                    {item.position ? (
                        <Stack
                            gap="4px"
                            flex="1"
                        >
                            <Text
                                fontVariant="body2"
                                color={TEXT_COLOR}
                            >
                                <strong>{translate("ad.intelligence.verification.drawer.detail.position")}</strong>
                            </Text>
                            <CapitalizedText fontVariant="body2">
                                {formatPositionLabel(
                                    item.position,
                                    translate("ad.intelligence.verification.filters.ad_position.unknown"),
                                )}
                            </CapitalizedText>
                        </Stack>
                    ) : null}
                </Flex>
                <Stack gap="4px">
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Text
                            fontVariant="body2"
                            color={TEXT_COLOR}
                        >
                            <strong>{translate("ad.intelligence.verification.drawer.detail.screenshot_label")}</strong>
                        </Text>
                        {item.screenshot ? (
                            <Flex
                                alignItems="center"
                                gap="4px"
                            >
                                <Tooltip
                                    content={translate("ad.intelligence.verification.drawer.screenshot.expand_tooltip")}
                                >
                                    <IconButton
                                        onClick={handleOpenPreview}
                                        data-automation="verification-screenshot-open"
                                    >
                                        <ExpandDiagonal size="xs" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    content={translate(
                                        "ad.intelligence.verification.drawer.screenshot.download_tooltip",
                                    )}
                                >
                                    <IconButton
                                        onClick={handleDownloadScreenshot}
                                        data-automation="verification-screenshot-download"
                                    >
                                        <Download size="xs" />
                                    </IconButton>
                                </Tooltip>
                            </Flex>
                        ) : null}
                    </Flex>
                    {item.screenshot ? (
                        <ScreenshotScrollArea>
                            <ScreenshotImage
                                src={item.screenshot}
                                loading="lazy"
                                alt={translate("ad.intelligence.verification.drawer.screenshot_alt")}
                                onClick={handleOpenPreview}
                            />
                        </ScreenshotScrollArea>
                    ) : (
                        <Text
                            fontVariant="body2"
                            color={TEXT_COLOR}
                        >
                            {translate("ad.intelligence.verification.drawer.screenshot.none")}
                        </Text>
                    )}
                </Stack>
            </DetailsContent>
        </StyledDetails>
    );
};

interface IVerificationDrawerBodyProps {
    records: AdIntelligenceRecordsDetailedVerification[];
    totalCount: number;
    tabCounts: Record<AdIntelligenceEnumsScreenshotFilter, number>;
    publisher: string;
    onPreview: (src: string) => void;
    page: number;
    onPageChange: (page: number) => void;
    activeTab: AdIntelligenceEnumsScreenshotFilter;
    onTabChange: (tab: AdIntelligenceEnumsScreenshotFilter) => void;
}

const VerificationDrawerBody = ({
    records,
    totalCount,
    tabCounts,
    publisher,
    onPreview,
    page,
    onPageChange,
    activeTab,
    onTabChange,
}: IVerificationDrawerBodyProps) => {
    const { translate } = useTranslate();

    const segmentedValues = useMemo(
        () =>
            [
                {
                    label: translate("ad.intelligence.verification.drawer.tab.all", {
                        count: formatCount(tabCounts.All),
                    }),
                    disabled: tabCounts.All === 0,
                },
                {
                    label: translate("ad.intelligence.verification.drawer.tab.screenshots", {
                        count: formatCount(tabCounts.HasScreenshot),
                    }),
                    icon: "image" as const,
                    disabled: tabCounts.HasScreenshot === 0,
                },
                {
                    label: translate("ad.intelligence.verification.drawer.tab.no_screenshots", {
                        count: formatCount(tabCounts.NoScreenshot),
                    }),
                    icon: "eye-icon-blue" as const,
                    disabled: tabCounts.NoScreenshot === 0,
                },
            ] as SegmentedControlValue[],
        [tabCounts, translate],
    );

    const handleViewModeChange = useCallback(
        (index: number) => {
            onTabChange(VERIFICATION_TAB_KEYS[index] ?? "All");
        },
        [onTabChange],
    );

    const selectedViewIndex = VERIFICATION_TAB_KEYS.indexOf(activeTab);

    const indexOffset = (page - 1) * ITEMS_PER_PAGE;

    return (
        <Stack gap="12px">
            <Text
                fontVariant="subtitle1"
                color={TEXT_COLOR}
            >
                {translate("ad.intelligence.verification.drawer.section_title")}
            </Text>
            <SegmentedControl
                selectedIndex={selectedViewIndex}
                values={segmentedValues}
                onChange={handleViewModeChange}
            />
            <Stack gap="8px">
                {records.map((item, index) => (
                    <VerificationDetailItem
                        key={`${item.dateTime}-${index}`}
                        item={item}
                        publisher={publisher}
                        onPreview={onPreview}
                        rowNumber={indexOffset + index + 1}
                    />
                ))}
            </Stack>
            {totalCount > ITEMS_PER_PAGE ? (
                <Pagination
                    page={page}
                    itemsCount={totalCount}
                    itemsPerPage={ITEMS_PER_PAGE}
                    handlePageChange={onPageChange}
                    captionPosition="center"
                    captionElement={PaginationInput}
                    hasItemsPerPageSelect={false}
                />
            ) : null}
        </Stack>
    );
};

export const VerificationDrawer = ({
    isOpen,
    onOpenChange,
    cellData,
    advertiser,
    position,
}: IVerificationDrawerProps) => {
    const { translate } = useTranslate();
    const { duration = "", screenshotsOnly } = useDisplayAdvertiserPublisherDataAndParams() ?? {};
    const durationData = DurationService.getDurationData(duration);

    const dateFrom = cellData?.dateFrom ?? durationData.forAPI.iso.from;
    const dateTo = cellData?.dateTo ?? durationData.forAPI.iso.to;

    const [currentPage, setCurrentPage] = useState(1);
    const defaultActiveTab = screenshotsOnly ? "HasScreenshot" : "All";
    const [activeTab, setActiveTab] = useState<AdIntelligenceEnumsScreenshotFilter>(defaultActiveTab);

    // Reset page + active tab whenever the selected cell changes.
    useEffect(() => {
        setCurrentPage(1);
        setActiveTab(defaultActiveTab);
    }, [cellData?.publisher, cellData?.dateFrom, cellData?.dateTo, defaultActiveTab]);

    const details = useVerificationDetails({
        advertiser,
        publisher: cellData?.publisher ?? "",
        dateFrom,
        dateTo,
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        activeTab,
        position,
        enabled: isOpen && Boolean(cellData?.publisher),
    });

    const records = details?.records?.[activeTab] ?? [];
    const tabCounts = details.totalCounts;

    const handleTabChange = useCallback((tab: AdIntelligenceEnumsScreenshotFilter) => {
        setActiveTab(tab);
        setCurrentPage(1);
    }, []);

    const [previewSrc, setPreviewSrc] = useState<string | null>(null);

    const handleOpenPreview = useCallback((src: string) => {
        setPreviewSrc(src);
    }, []);

    const handleClosePreview = useCallback(() => {
        setPreviewSrc(null);
    }, []);

    const isPreviewOpen = previewSrc !== null;

    const handleDrawerOpenChange = useCallback(
        (open: boolean) => {
            if (!open && isPreviewOpen) {
                return;
            }
            onOpenChange(open);
        },
        [isPreviewOpen, onOpenChange],
    );

    const { publisher, dateFrom: cellDateFrom, dateTo: cellDateTo } = cellData ?? {};

    const bodyKey = cellData ? `${publisher}|${cellDateFrom ?? ""}|${cellDateTo ?? ""}` : "empty";

    const publisherTitle = cellData?.publisher || translate("ad.intelligence.verification.drawer.default_title");
    const titleDate = publisher && cellDateFrom && cellDateTo ? `(${formatDateRange(cellDateFrom, cellDateTo)})` : "";

    return (
        <>
            <Drawer
                isOpen={isOpen}
                onOpenChange={handleDrawerOpenChange}
            >
                <Drawer.Header
                    // @ts-expect-error: title accepts react components, the error/field type is incorrect
                    title={
                        <Flex
                            gap="8px"
                            alignItems="center"
                        >
                            {cellData?.publisher ? <Favicon src={getFaviconUrl(cellData?.publisher)} /> : null}
                            <span>{publisherTitle}</span>
                            <span>{titleDate}</span>
                        </Flex>
                    }
                />
                <Drawer.Content>
                    {details.isFetching ? (
                        <VerificationDrawerSkeleton itemsCount={clampSkeletonCount(cellData?.expectedCount)} />
                    ) : (
                        <VerificationDrawerBody
                            key={bodyKey}
                            records={records}
                            totalCount={tabCounts[activeTab]}
                            tabCounts={tabCounts}
                            publisher={cellData?.publisher ?? ""}
                            onPreview={handleOpenPreview}
                            page={currentPage}
                            onPageChange={setCurrentPage}
                            activeTab={activeTab}
                            onTabChange={handleTabChange}
                        />
                    )}
                </Drawer.Content>
            </Drawer>
            <ImagePreviewDialog
                isOpen={previewSrc !== null}
                onClose={handleClosePreview}
                src={previewSrc}
                alt={translate("ad.intelligence.verification.drawer.screenshot_preview_alt")}
            />
        </>
    );
};
